import { fncClassName } from "@/lib/utils/fncClassName";
import type { Node } from "./types";

export const financialsTreeDataInduTemplate: Node[] = [
	{
		name: "Financial Metrics",
		children: [
			{
				name: "Valuation",
				children: [
					{
						field: "calculations.marketcap",
						name: "Market Capitalization",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.enterprisevalue",
						name: "Enterprise Value",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{ field: "calculations.pricetobook", name: "Price to Book" },
					{
						field: "calculations.earningsyield",
						name: "Earnings Yiel",
						options: { style: "percent" },
					},
					{
						field: "calculations.dividendyield",
						name: "Dividend Yield",
						options: { style: "percent" },
					},
					{ field: "calculations.pricetoearnings", name: "Price to Earnings" },
					{
						field: "calculations.pricetotangiblebook",
						name: "Price to Tangible Book Value",
					},
					{ field: "calculations.pricetorevenue", name: "Price to Revenue" },
					{
						field: "calculations.evtoinvestedcapital",
						name: "EV to Invested Capital",
					},
					{ field: "calculations.evtorevenue", name: "EV to Revenue" },
					{ field: "calculations.evtoebit", name: "EV to EBIT" },
					{ field: "calculations.evtonopat", name: "EV to NOPAT" },
					{ field: "calculations.evtoebitda", name: "EV to EBITDA " },
					{ field: "calculations.evtoocf", name: "EV to Operating Cash Flow" },
					{
						field: "calculations.evtofcff",
						name: "EV to Free Cash Flow to Firm",
					},
				],
			},
			{
				name: "Growth Rates",
				children: [
					{
						field: "calculations.revenuegrowth",
						name: "Revenue Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.revenueqoqgrowth",
						name: "Revenue Q/Q Growth",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitdagrowth",
						name: "EBITDA Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitdaqoqgrowth",
						name: "EBITDA Q/Q Growth",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.nopatgrowth",
						name: "NOPAT Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.nopatqoqgrowth",
						name: "NOPAT Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitgrowth",
						name: "EBIT Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ebitqoqgrowth",
						name: "EBIT Q/O",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.netincomegrowth",
						name: "Net Income Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.netincomeqoqgrowth",
						name: "Net Income Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.epsgrowth",
						name: "EPS Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.epsqoqgrowth",
						name: "EPS Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ocfgrowth",
						name: "Operating Cash Flow Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.ocfqoqgrowth",
						name: "Operating Cash Flow Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.investedcapitalgrowth",
						name: "Invested Capital Y/Y",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.investedcapitalqoqgrowth",
						name: "Invested Capital Q/Q",
						options: { style: "percent" },
						fncClassName,
					},
				],
			},
			{
				name: "Profitability Margins",
				children: [
					{
						field: "calculations.grossmargin",
						name: "Gross Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.ebitdamargin",
						name: "EBITDA Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.operatingmargin",
						name: "Operating Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.nopatmargin",
						name: "NOPAT Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.costofrevtorevenue",
						name: "Cost of Revenue to Revenue",
						options: { style: "percent" },
					},
					{
						field: "calculations.sgaextorevenue",
						name: "SG&A Expenses to Revenue",
						options: { style: "percent" },
					},
					{
						field: "calculations.rdextorevenue",
						name: "R&D to Revenue",
						options: { style: "percent" },
					},
					{
						field: "calculations.opextorevenue",
						name: "Operating Expenses to Revenue",
						options: { style: "percent" },
					},
					{
						field: "calculations.efftaxrate",
						name: "Effective Tax Rate",
						options: { style: "percent" },
					},
				],
			},
			{
				name: "Metrics",
				children: [
					{
						field: "calculations.nopat",
						name: "Net Operating Profit After Taxes (NOPAT)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.ebitda",
						name: "EBITDA",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.ebit",
						name: "EBIT",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.freecashflow",
						name: "Free Cash Flow",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.investedcapital",
						name: "Invested Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.netnonopobligations",
						name: "Net Nonoperating Obligations",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "calculations.debt",
						name: "Total Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.longtermdebt",
						name: "Long-Term Debt ",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.netdebt",
						name: "Net Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "calculations.totalcapital",
						name: "Total Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "calculations.bookvaluepershare",
						name: "Book Value per Share",
						options: { style: "currency", currency: "USD" },
					},
					{
						field: "calculations.tangbookvaluepershare",
						name: "Tangible Book Value per Share",
						options: { style: "currency", currency: "USD" },
					},
					{
						field: "calculations.investedcapitalincreasedecrease",
						name: "Increase (Decrease) in Invested Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "calculations.capex",
						name: "Capital Expenditures (CapEx)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						name: "Depreciation & Amortization Expense Debt-free",
					},
					{
						field: "calculations.dfcfnwc",
						name: "Cash-free Debt-free Net Working Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "calculations.nwc",
						name: "Net Working Capital",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
				],
			},
			{
				name: "FOIC / ROE Disaggregation",
				children: [
					{
						field: "calculations.normalizednopatmargin",
						name: "Net Operating Profit After Tax Margin (NOPAT N",
						options: { style: "percent" },
					},
					{
						field: "calculations.investedcapitalturnover",
						name: "Invested Capital Turnover",
						fncClassName,
					},
					{
						field: "calculations.roic",
						name: "Return on Invested Capital (ROIC)",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.finleverage",
						name: "Financial Leverage",
						fncClassName,
					},
					{
						field: "calculations.nnep",
						name: "Net Nonoperating Expense Percent",
						options: { style: "percent" },
					},
					{
						field: "calculations.roicnnepspread",
						name: "ROIC-NNEP Spread",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.rnnoa",
						name: "Return on Net Nonoperating Assets (RNNOA)",
						options: { style: "percent" },
					},
					{
						field: "calculations.roe",
						name: "Return on Equity (ROE)",
						options: { style: "percent" },
					},
					{
						field: "calculations.profitmargin",
						name: "Profit Margin",
						options: { style: "percent" },
					},
					{
						field: "calculations.taxburdenpct",
						name: "Tax Burden Percent",
						options: { style: "percent" },
					},
					{
						field: "calculations.interestburdenpct",
						name: "Interest Burden Percent",
						options: { style: "percent" },
					},
					{
						field: "calculations.ebitmargin",
						name: "EBIT Margin",
						options: { style: "percent" },
					},
					{ field: "calculations.assetturnover", name: "Asset Turnover" },
					{ field: "calculations.leverageratio", name: "Leverage Ratio" },
					{
						field: "calculations.oroa",
						name: "Operating Return on Assets (OROA)",
						options: { style: "percent" },
					},
					{
						field: "calculations.roa",
						name: "Return on Assets (ROA)",
						options: { style: "percent" },
					},
					{
						field: "calculations.compoundleveragefactor",
						name: "Compound Leverage Factor",
					},
					{
						field: "calculations.noncontrollinginterestsharingratio",
						name: "Noncontrolling Interest Sharing Ratio",
						options: { style: "percent" },
					},
					{
						field: "calculations.roce",
						name: "Return on Common Equity (ROCE)",
						options: { style: "percent" },
					},
					{
						field: "calculations.croic",
						name: "Cash Return on Invested Capital (CROIC)",
						options: { style: "percent" },
					},
				],
			},
			{
				name: "Effectiveness",
				children: [
					{
						field: "calculations.divpayoutratio",
						name: "Dividend Payout Ratio",
						options: { style: "percent" },
					},
					{
						field: "calculations.augmentedpayoutratio",
						name: "Augmented Payout Ratio",
						options: { style: "percent" },
					},
					{
						field: "calculations.ocftocapex",
						name: "Operating Cash Flow to CapEx",
						options: { style: "percent" },
					},
				],
			},

			{
				name: "Efficiencies",
				children: [
					{
						field: "calculations.investedcapitalturnover",
						name: "Invested Capital Turnover",
						fncClassName,
					},
					{ field: "calculations.assetturnover", name: "Asset Turnover" },
					{
						field: "calculations.arturnover",
						name: "Accounts Receivable Turnover",
					},
					{ field: "calculations.invturnover", name: "Inventory Turnover" },
					{ field: "calculations.faturnover", name: "Fixed Asset Turnover" },
					{
						field: "calculations.apturnover",
						name: "Accounts Payable Turnover",
					},
					{ field: "calculations.dso", name: "Days Sales Outstanding (DSO)" },
					{
						field: "calculations.dio",
						name: "Days Inventory Outstanding (DIO)",
					},
					{ field: "calculations.dpo", name: "Days Payable Outstanding (DPO)" },
					{
						field: "calculations.ccc",
						name: "Cash Conversion Cycle (CCC)",
						fncClassName,
					},
				],
			},
			{
				name: "Leverage",
				children: [
					{ field: "calculations.debttoequity", name: "Debt to Equity" },
					{
						field: "calculations.ltdebttoequity",
						name: "Long-Term Debt to Equity",
					},
				],
			},
			{
				name: "Capital Structure",
				children: [
					{
						field: "calculations.stdebttocap",
						name: "Short-Term Debt to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.ltdebttocap",
						name: "Long-Term Debt to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.debttototalcapital",
						name: "Debt to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.preferredtocap",
						name: "Preferred Equity to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.noncontrolinttocap",
						name: "Noncontrolling Interests to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "calculations.commontocap",
						name: "Common Equity to Total Capital",
						options: { style: "percent" },
					},
				],
			},
			{
				name: "Solvency",
				children: [
					{
						field: "calculations.debttoebitda",
						name: "Debt to EBITDA",
						fncClassName,
					},
					{
						field: "calculations.netdebttoebitda",
						name: "Net Debt to EBITDA",
						fncClassName,
					},
					{
						field: "calculations.ltdebttoebitda",
						name: "Long-Term Debt to EBITDA",
						fncClassName,
					},
					{
						field: "calculations.debttonopat",
						name: "Debt to NOPAT",
						fncClassName,
					},
					{
						field: "calculations.netdebttonopat",
						name: "Net Debt to NOPAT",
						fncClassName,
					},
					{
						field: "calculations.ltdebttonopat",
						name: "Long-Term Debt to NOPAT",
						fncClassName,
					},
					{
						field: "calculations.altmanzscore",
						name: "Altman Z-Score",
						fncClassName,
					},
				],
			},

			{
				name: "Coverage",
				children: [
					{
						field: "calculations.ebittointerestex",
						name: "EBIT to Interest Expense",
					},
					{
						field: "calculations.nopattointerestex",
						name: "NOPAT to Interest Expense",
					},
					{
						field: "calculations.fcfftointerestex",
						name: "Free Cash Flow to Firm to Interest Expense",
					},
					{
						field: "calculations.ocftointerestex",
						name: "Operating Cash Flow to Interest Expense",
					},
					{
						field: "calculations.ebitlesscapextointerestex",
						name: "EBIT Less CapEx to Interest Expense",
					},
					{
						field: "calculations.nopatlesscapextointex",
						name: "NOPAT Less CapEx to Interest Expense",
					},
					{
						field: "calculations.ocflesscapextointerestex",
						name: "Operating Cash Flow Less CapEx to Interest Exp",
					},
				],
			},
			{
				name: "Liquidity",
				children: [
					{ field: "calculations.currentratio", name: "Current Ratio" },
					{ field: "calculations.quickratio", name: "Quick Ratio" },
					{
						field: "calculations.dfcfnwctorev",
						name: "Debt-free, Cash-free Net Working Capital to Revenue",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.dfnwctorev",
						name: "Debt-free Net Working Capital to Revenue",
						options: { style: "percent" },
						fncClassName,
					},
					{
						field: "calculations.nwctorev",
						name: "Net Working Capital to Revenue",
						options: { style: "percent" },
						fncClassName,
					},
				],
			},
		],
	},

	{
		name: "Income Statement",
		children: [
			{
				field: "income_statement.totalrevenue",
				name: "Total Revenue",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.operatingrevenue",
						name: "Operating Revenue",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.otherrevenue",
						name: "Other Revenue",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.totalcostofrevenue",
				name: "Total Cost of Revenue",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.operatingcostofrevenue",
						name: "Operating Cost of Revenue",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.othercostofrevenue",
						name: "Other Cost of Revenue",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.totalgrossprofit",
				name: "Total Gross Profit",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.totaloperatingexpenses",
				name: "Total Operating Expenses",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.sgaexpense",
						name: "Selling, General & Admin Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.marketingexpense",
						name: "Marketing Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.rdexpense",
						name: "Research & Development Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.explorationexpense",
						name: "Exploration Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.depreciationexpense",
						name: "Depreciation Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.amortizationexpense",
						name: "Amortization Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.depletionexpense",
						name: "Depletion Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.otherincome",
						name: "Other Operating Expenses / (Income)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "income_statement.impairmentexpense",
						name: "Impairment Charge",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.restructuringcharge",
						name: "Restructuring Charge",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.otherspecialcharges",
						name: "Other Special Charges ",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.totaloperatingincome",
				name: "Total Operating Income",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.totalinterestexpense",
						name: "Total Interest Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.investmentsecuritiesinterestincome",
						name: "Investment Securities Interest Income",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "calculations.netnonopex",
				name: "Net Non-Operating Expense",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "balance_sheet_statement.shortterminvestments",
						name: "Short-Term Investments",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "income_statement.totalpretaxincome",
				name: "Total Pre-Tax Income",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
			{
				field: "income_statement.netincometocommon",
				name: "Net Income / (Loss) Attributable to Common Shareholders",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.incometaxexpense",
						name: "Income Tax Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.othergains",
						name: "Other Gains / (Losses), net",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netincomecontinuing",
						name: "Net Income / (Loss) Continuing Operations",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netincomediscontinued",
						name: "Net Income / (Loss) Discontinued Operations",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.extraordinaryincome",
						name: "Extraordinary Income / (Loss), net",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.otheradjustmentstoconsolidatednetincome",
						name: "Other Adjustments to Consolidated Net Income / (L",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netincome",
						name: "Consolidated Net Income / (Loss)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.preferreddividends",
						name: "Preferred Stock Dividends Declared",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netincometononcontrollinginterest",
						name: "Net Income / (Loss) Attributable to Noncontrolling Interest",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.otheradjustmentstoconsolidatednetincome",
						name: "Other Adjustments to Net Income / (Loss)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				name: "Common Shareholders",
				children: [
					{
						field: "income_statement.basiceps",
						name: "Basic Earnings per Share",
						options: { style: "currency", currency: "USD" },
					},
					{
						field: "income_statement.weightedavebasicsharesos",
						name: "Weighted Average Basic Shares Outstanding",
					},
					{
						field: "income_statement.dilutedeps",
						name: "Diluted Earnings per Share",
						options: { style: "currency", currency: "USD" },
					},
					{
						field: "income_statement.weightedavedilutedsharesos",
						name: "Weighted Average Diluted Shares Outstanding",
					},
					{ name: "Dividends" },
					{
						field: "income_statement.cashdividendspershare",
						name: "Cash Dividends to Common per Share",
						options: { style: "currency", currency: "USD" },
					},
				],
			},
		],
	},

	{
		name: "Balance Sheet",
		children: [
			{
				field: "balance_sheet_statement.totalassets",
				name: "Total Asset ",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "balance_sheet_statement.totalcurrentassets",
						name: "Total Current Assets",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						children: [
							{
								field: "balance_sheet_statement.cashandequivalents",
								name: "Cash & Equivalents",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.restrictedcash",
								name: "Restricted Cash",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.timedepositsplaced",
								name: "Short-Term Investments",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.noncurrentnotereceivables",
								name: "Note & Lease Receivable",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.accountsreceivable",
								name: "Accounts Receivable",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.netinventory",
								name: "Inventories, net",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.prepaidexpenses",
								name: "Prepaid Expenses",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.currentdeferredtaxassets",
								name: "Current Deferred & Refundable Income Taxes",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.othercurrentassets",
								name: "Other Current Assets",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "calculations.rnnoa",
								name: "Other Current Non-Operating Assets",
								options: { style: "percent" },
							},
						],
					},
					{
						field: "balance_sheet_statement.netppe",
						name: "Plant, Property, & Equipment, net",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						children: [
							{
								field: "balance_sheet_statement.grossppe",
								name: "Plant, Property & Equipment, gross",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.accumulateddepreciation",
								name: "Accumulated Depreciation",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
						],
					},
					{
						field: "balance_sheet_statement.totalnoncurrentassets",
						name: "Total Noncurrent Assets",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						children: [
							{ name: "Other Non-Current Assets" },
							{
								field: "balance_sheet_statement.longterminvestments",
								name: "Long-Term Investments",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.noncurrentnotereceivables",
								name: "Noncurrent Note & Lease Receivables",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.goodwill",
								name: "Goodwill",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.intangibleassets",
								name: "Intangible Assets",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.noncurrentdeferredtaxassets",
								name: "Noncurrent Deferred & Refundable Income Taxes",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.employeebenefitassets",
								name: "Employee Benefit Assets",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.othernoncurrentassets",
								name: "Other Noncurrent Operating Assets",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
						],
					},
				],
			},
			{
				field: "balance_sheet_statement.totalliabilities",
				name: "Total Liabilities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "balance_sheet_statement.totalcurrentliabilities",
						name: "Total Current Liabilities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						children: [
							{ name: "Current Liabilities" },
							{
								field: "balance_sheet_statement.shorttermdebt",
								name: "Short-Term Debt",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.accountspayable",
								name: "Accounts Payable",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.accruedexpenses",
								name: "Accrued Expenses",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.customerdeposits",
								name: "Customer Deposits",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.dividendspayable",
								name: "Dividends Payable",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.currentdeferredrevenue",
								name: "Current Deferred Revenue",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.currentdeferredtaxliabilities",
								name: "Current Deferred & Payable Income Tax Liabilitie",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field:
									"balance_sheet_statement.currentemployeebenefitliabilities",
								name: "Current Employee Benefit Liabilities",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.othertaxespayable",
								name: "Other Taxes Payable",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.othercurrentliabilities",
								name: "Other Current Liabilities",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
						],
					},
					{
						field: "balance_sheet_statement.totalnoncurrentliabilities",
						name: "Total Noncurrent Liabilities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						children: [
							{
								field: "balance_sheet_statement.longtermdebt",
								name: "Long-Term Debt",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.capitalleaseobligations",
								name: "Capital Lease Obligations",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field:
									"balance_sheet_statement.assetretirementandlitigationobligation",
								name: "Asset Retirement Reserve & Litigation Obligation",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.noncurrentdeferredrevenue",
								name: "Noncurrent Deferred Revenue",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field:
									"balance_sheet_statement.noncurrentemployeebenefitliabilities",
								name: "Noncurrent Employee Benefit Liabilities",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
							{
								field: "balance_sheet_statement.othernoncurrentliabilities",
								name: "Other Noncurrent Operating Liabilities",
								options: {
									notation: "compact",
									style: "currency",
									currency: "USD",
									maximumFractionDigits: 3,
								},
							},
						],
					},
				],
			},
			{
				field: "balance_sheet_statement.totalequity",
				name: "Total Equity",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "calculations.preferredtocap",
						name: "Preferred Equity to Total Capital",
						options: { style: "percent" },
					},
					{
						field: "balance_sheet_statement.totalpreferredequity",
						name: "Preferred Stock",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.totalcommonequity",
						name: "Total Common Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.commonequity",
						name: "Common Stock",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.retainedearnings",
						name: "Retained Earnings",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.treasurystock",
						name: "Treasury Stock",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "balance_sheet_statement.aoci",
						name: "Accumulated Other Comprehensive Income / (Los",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "balance_sheet_statement.totalliabilitiesandequity",
				name: "Total Liabilities & Shareholders Equity",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
			},
		],
	},

	{
		name: "Cash Flow",
		children: [
			{
				field: "cash_flow_statement.netcashfromoperatingactivities",
				name: "Net Cash From Operating Activities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				children: [
					{
						field: "income_statement.netincome",
						name: "Consolidated Net Income (Loss)",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netincomediscontinued",
						name: "Net Income (Loss) Discontinued Operations",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.netincomecontinuing",
						name: "Net Income / (Loss) Continuing Operatio",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.depreciationexpense",
						name: "Depreciation Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "income_statement.amortizationexpense",
						name: "Amortization Expense",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field:
							"cash_flow_statement.netcashfromcontinuinginvestingactivities",
						name: "Net Cash From Continuing Investing Activities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field:
							"cash_flow_statement.netcashfromdiscontinuedoperatingactivities",
						name: "Net Cash From Discontinued Operating",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},

			{
				field: "cash_flow_statement.netcashfrominvestingactivities",
				name: "Net Cash From Investing Activities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "cash_flow_statement.purchaseofplantpropertyandequipment",
						name: "Purchase of Property, Leasehold Improvements and Equipment",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.acquisitions",
						name: "Acquisitions",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.purchaseofinvestments",
						name: "Purchase of Investments",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.saleofplantpropertyandequipment",
						name: "Sale of Property, Plant & Equipment",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.divestitures",
						name: "Divestitures",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field:
							"cash_flow_statement.netcashfromcontinuinginvestingactivities",
						name: "Net Cash From Continuing Investing Act.",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field:
							"cash_flow_statement.netcashfromdiscontinuedinvestingactivities",
						name: "Net Cash From Discontinued Investing",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "cash_flow_statement.netcashfromfinancingactivities",
				name: "Net Cash From Financing Activities",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "cash_flow_statement.repaymentofdebt",
						name: "Repayment of Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.repurchaseofpreferredequity",
						name: "Repurchase of Preferred Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.repurchaseofcommonequity",
						name: "Repurchase of Common Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.paymentofdividends",
						name: "Payment of Dividends",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field: "cash_flow_statement.issuanceofdebt",
						name: "Issuance of Debt",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.issuanceofpreferredequity",
						name: "Issuance of Preferred Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.issuanceofcommonequity",
						name: "Issuance of Common Equity",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.otherfinancingactivitiesnet",
						name: "Other Financing Activities, Net",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field:
							"cash_flow_statement.netcashfromcontinuingfinancingactivities",
						name: "Net Cash From Continuing Financing Activities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
						fncClassName,
					},
					{
						field:
							"cash_flow_statement.netcashfromdiscontinuedfinancingactivities",
						name: "Net Cash From Discontinued Financing Activities",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
			{
				field: "calculations.fcffqoqgrowth",
				name: "Free Cash Flow Firm Q/Q Growth",
				options: { style: "percent" },
				fncClassName,
			},
			{
				field: "calculations.fcffgrowth",
				name: "Free Cash Flow Firm Growth",
				options: { style: "percent" },
				fncClassName,
			},
			{
				field: "cash_flow_statement.netchangeincash",
				name: "Net Change in Cash & Equivalents",
				options: {
					notation: "compact",
					style: "currency",
					currency: "USD",
					maximumFractionDigits: 3,
				},
				fncClassName,
				children: [
					{
						field: "cash_flow_statement.effectofexchangeratechanges",
						name: "Effect of Exchange Rate Changes",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.othernetchangesincash",
						name: "Other Net Changes in Cash",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.cashincometaxespaid",
						name: "Cash Income Taxes Paid",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.cashinterestpaid",
						name: "Cash Interest Paid",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
					{
						field: "cash_flow_statement.cashinterestreceived",
						name: "Cash Interest Received",
						options: {
							notation: "compact",
							style: "currency",
							currency: "USD",
							maximumFractionDigits: 3,
						},
					},
				],
			},
		],
	},
];
